import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash2, X, Users, MessageSquareText, Smartphone, Loader2 } from "lucide-react";
import api, { formatError } from "../lib/api";

/**
 * Segment Builder — visual AND/OR filter tree with live audience preview.
 *
 * Filter tree shape:
 *   { op: "and" | "or", rules: [ rule | group, ... ] }
 *   rule  = { field, operator, value }
 *   group = { op, rules: [...] }
 */

const blankRule = (field) => ({ field: field || "state", operator: "in", value: [] });
const blankGroup = (op = "and") => ({ op, rules: [] });

// --------------------------------------------------------------------------
// Rule editor — renders the right input control for the field's data type
// --------------------------------------------------------------------------
function RuleEditor({ rule, fieldDefs, options, onChange, onRemove }) {
  const def = fieldDefs.find((f) => f.id === rule.field) || fieldDefs[0];
  const ops = def?.ops || ["equals"];
  const opts = def?.options || options[def?.id] || [];

  // When field changes, reset operator + value to sensible defaults
  function setField(fid) {
    const fd = fieldDefs.find((f) => f.id === fid);
    let nextVal = "";
    if (fd.type === "enum" || fd.type === "text") nextVal = fd.type === "enum" ? [] : "";
    else if (fd.type === "number") nextVal = 0;
    else if (fd.type === "date") nextVal = "";
    else if (fd.type === "bool") nextVal = true;
    else if (fd.type === "window_count") nextVal = { from: "", to: "", count: 1 };
    onChange({ field: fid, operator: fd.ops[0], value: nextVal });
  }

  function setOp(op) {
    let nextVal = rule.value;
    if (op === "between" && def.type === "number" && !Array.isArray(rule.value)) nextVal = [0, 100];
    if ((op === "is_true" || op === "is_false") && def.type === "bool") nextVal = null;
    if (op === "between" && def.type === "date") nextVal = ["", ""];
    onChange({ ...rule, operator: op, value: nextVal });
  }

  function setValue(v) {
    onChange({ ...rule, value: v });
  }

  const grouped = useMemo(() => {
    const m = {};
    fieldDefs.forEach((f) => {
      m[f.group] = m[f.group] || [];
      m[f.group].push(f);
    });
    return m;
  }, [fieldDefs]);

  return (
    <div className="flex items-start gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2.5" data-testid="rule-row">
      {/* Field */}
      <select
        value={rule.field}
        onChange={(e) => setField(e.target.value)}
        className="text-[13px] py-1.5 min-w-[200px]"
        data-testid="rule-field"
      >
        {Object.entries(grouped).map(([g, list]) => (
          <optgroup key={g} label={g}>
            {list.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </optgroup>
        ))}
      </select>

      {/* Operator */}
      <select value={rule.operator} onChange={(e) => setOp(e.target.value)} className="text-[13px] py-1.5" data-testid="rule-op">
        {ops.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
      </select>

      {/* Value */}
      <div className="flex-1">
        <ValueInput def={def} options={opts} operator={rule.operator} value={rule.value} onChange={setValue} />
      </div>

      <button type="button" onClick={onRemove} className="text-slate-400 hover:text-rose-600 p-1" data-testid="rule-remove">
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function ValueInput({ def, options, operator, value, onChange }) {
  if (!def) return null;
  // Boolean ops have no value input
  if (operator === "is_true" || operator === "is_false") return <div className="text-[12px] text-slate-400 py-1.5">—</div>;

  if (def.type === "enum" || (def.type === "text" && (operator === "in" || operator === "not_in"))) {
    return (
      <MultiSelect
        options={options}
        value={Array.isArray(value) ? value : []}
        onChange={onChange}
      />
    );
  }
  if (def.type === "text") {
    return <input className="text-[13px] py-1.5 w-full" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="rule-value" />;
  }
  if (def.type === "number") {
    if (operator === "between") {
      const [lo, hi] = Array.isArray(value) ? value : [0, 0];
      return (
        <div className="flex items-center gap-1.5">
          <input type="number" value={lo} onChange={(e) => onChange([Number(e.target.value), hi])} className="text-[13px] py-1.5 w-24" placeholder="min" />
          <span className="text-slate-400 text-[12px]">to</span>
          <input type="number" value={hi} onChange={(e) => onChange([lo, Number(e.target.value)])} className="text-[13px] py-1.5 w-24" placeholder="max" />
        </div>
      );
    }
    return <input type="number" value={value || 0} onChange={(e) => onChange(Number(e.target.value))} className="text-[13px] py-1.5 w-32" data-testid="rule-value" />;
  }
  if (def.type === "date") {
    if (operator === "between") {
      const [lo, hi] = Array.isArray(value) ? value : ["", ""];
      return (
        <div className="flex items-center gap-1.5">
          <input type="date" value={lo} onChange={(e) => onChange([e.target.value, hi])} className="text-[13px] py-1.5" />
          <span className="text-slate-400 text-[12px]">to</span>
          <input type="date" value={hi} onChange={(e) => onChange([lo, e.target.value])} className="text-[13px] py-1.5" />
        </div>
      );
    }
    if (operator === "in_last_days") {
      return (
        <div className="flex items-center gap-1.5">
          <input type="number" value={value || 30} onChange={(e) => onChange(Number(e.target.value))} className="text-[13px] py-1.5 w-24" />
          <span className="text-[12px] text-slate-500">days</span>
        </div>
      );
    }
    return <input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="text-[13px] py-1.5" />;
  }
  if (def.type === "window_count") {
    const v = value || { from: "", to: "", count: 1 };
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <input type="date" value={v.from} onChange={(e) => onChange({ ...v, from: e.target.value })} className="text-[13px] py-1.5" />
        <span className="text-slate-400 text-[12px]">→</span>
        <input type="date" value={v.to} onChange={(e) => onChange({ ...v, to: e.target.value })} className="text-[13px] py-1.5" />
        <span className="text-slate-400 text-[12px]">count {operator}</span>
        <input type="number" value={v.count} onChange={(e) => onChange({ ...v, count: Number(e.target.value) })} className="text-[13px] py-1.5 w-20" />
      </div>
    );
  }
  return null;
}

function MultiSelect({ options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const filtered = useMemo(
    () => (options || []).filter((o) => !q || String(o).toLowerCase().includes(q.toLowerCase())),
    [options, q]
  );
  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);
  function toggle(opt) {
    const has = value.includes(opt);
    onChange(has ? value.filter((v) => v !== opt) : [...value, opt]);
  }
  return (
    <div className="relative" ref={ref}>
      <div
        onClick={() => setOpen((o) => !o)}
        className="text-[13px] border border-slate-300 rounded-md bg-white px-2 py-1.5 min-h-[34px] cursor-pointer flex flex-wrap items-center gap-1"
        data-testid="rule-multiselect"
      >
        {value.length === 0 && <span className="text-slate-400">Select values…</span>}
        {value.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 bg-teal-50 text-teal-800 text-[11px] rounded px-1.5 py-0.5">
            {v}
            <button type="button" onClick={(e) => { e.stopPropagation(); toggle(v); }} className="hover:text-rose-700"><X size={10} /></button>
          </span>
        ))}
      </div>
      {open && (
        <div className="absolute z-30 top-[calc(100%+4px)] left-0 right-0 max-h-64 overflow-auto bg-white border border-slate-200 rounded-md shadow-lg p-2">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="text-[12px] py-1 w-full mb-1"
          />
          {filtered.length === 0 && <div className="text-[12px] text-slate-400 px-2 py-1">No options</div>}
          {filtered.map((o) => (
            <label key={o} className="flex items-center gap-2 px-1.5 py-1 hover:bg-slate-50 rounded text-[12.5px] cursor-pointer">
              <input type="checkbox" checked={value.includes(o)} onChange={() => toggle(o)} />
              <span>{o}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Group node — renders rules + nested groups with AND/OR toggle
// --------------------------------------------------------------------------
function GroupNode({ node, fieldDefs, options, onChange, onRemove, depth = 0 }) {
  function update(idx, val) {
    const next = { ...node, rules: node.rules.map((r, i) => (i === idx ? val : r)) };
    onChange(next);
  }
  function remove(idx) {
    onChange({ ...node, rules: node.rules.filter((_, i) => i !== idx) });
  }
  function addRule() {
    onChange({ ...node, rules: [...node.rules, blankRule(fieldDefs[0]?.id)] });
  }
  function addGroup() {
    onChange({ ...node, rules: [...node.rules, blankGroup("or")] });
  }
  function setOp(op) {
    onChange({ ...node, op });
  }

  return (
    <div
      className={`rounded-xl ${depth === 0 ? "" : "border border-dashed border-slate-300 bg-slate-50/60 p-3"}`}
      data-testid={`group-${depth}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center bg-slate-100 rounded-md p-0.5">
          <button
            type="button"
            onClick={() => setOp("and")}
            className={`text-[11px] px-2.5 py-1 rounded ${node.op === "and" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
            data-testid={`group-${depth}-and`}
          >AND</button>
          <button
            type="button"
            onClick={() => setOp("or")}
            className={`text-[11px] px-2.5 py-1 rounded ${node.op === "or" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
            data-testid={`group-${depth}-or`}
          >OR</button>
          <span className="text-[10.5px] text-slate-400 px-2">Match {node.op === "and" ? "ALL" : "ANY"} of the following</span>
        </div>
        {depth > 0 && (
          <button type="button" onClick={onRemove} className="text-slate-400 hover:text-rose-600 text-[12px] inline-flex items-center gap-1">
            <X size={12} /> Remove group
          </button>
        )}
      </div>

      <div className="space-y-2">
        {node.rules.length === 0 && (
          <div className="text-[12.5px] text-slate-400 italic px-1 py-2">No filters yet — add a rule or sub-group below.</div>
        )}
        {node.rules.map((r, i) => (
          r.rules ? (
            React.createElement(GroupNode, { key: i, node: r, fieldDefs, options, onChange: (v) => update(i, v), onRemove: () => remove(i), depth: depth + 1 })
          ) : (
            <RuleEditor key={i} rule={r} fieldDefs={fieldDefs} options={options} onChange={(v) => update(i, v)} onRemove={() => remove(i)} />
          )
        ))}
      </div>

      <div className="flex items-center gap-2 mt-3">
        <button type="button" onClick={addRule} className="text-[12px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-teal-700 text-white hover:bg-teal-800" data-testid={`group-${depth}-add-rule`}>
          <Plus size={12} /> Add filter
        </button>
        {depth < 2 && (
          <button type="button" onClick={addGroup} className="text-[12px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-white" data-testid={`group-${depth}-add-group`}>
            <Plus size={12} /> Add group ({node.op === "and" ? "OR" : "AND"})
          </button>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Top-level builder + preview panel
// --------------------------------------------------------------------------
export default function SegmentBuilder({ value, onChange, onPreview }) {
  const [meta, setMeta] = useState({ fields: [], options: {} });
  const [tree, setTree] = useState(value || blankGroup("and"));
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState("");

  // Load field registry once
  useEffect(() => {
    (async () => {
      try {
        // Cache-bust to dodge any stale CDN/Safari cache between deploys
        const { data } = await api.get(`/segments/fields?_=${Date.now()}`);
        setMeta(data);
      } catch (e) {
        const status = e?.response?.status;
        const body = e?.response?.data;
        const msg = `Could not load filter library (HTTP ${status || "network"}). ${formatError(e)} — please hard-refresh.`;
        setError(msg);
        // eslint-disable-next-line no-console
        console.error("[SegmentBuilder] /segments/fields failed", { status, body, error: e });
      } finally {
        setLoadingMeta(false);
      }
    })();
  }, []);

  // Sync up
  useEffect(() => {
    onChange && onChange(tree);
  }, [tree]); // eslint-disable-line

  // Debounced live preview
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        setPreviewing(true);
        const { data } = await api.post("/segments/preview", { filters: tree });
        setPreview(data);
        onPreview && onPreview(data);
      } catch (e) {
        setError(formatError(e));
      } finally {
        setPreviewing(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [tree]); // eslint-disable-line

  if (loadingMeta) {
    return <div className="flex items-center gap-2 text-slate-500 text-[13px] p-6"><Loader2 size={14} className="animate-spin" /> Loading filter library…</div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-3">
        <div className="surface p-4">
          <GroupNode node={tree} fieldDefs={meta.fields} options={meta.options} onChange={setTree} />
        </div>
        {error && <div className="text-[12px] text-rose-700">{error}</div>}
      </div>

      <div className="lg:col-span-1">
        <div className="surface p-4 sticky top-4">
          <div className="flex items-center justify-between">
            <div className="small-caps text-slate-500">Live audience</div>
            {previewing && <Loader2 size={12} className="animate-spin text-teal-700" />}
          </div>
          <div className="serif-display text-4xl numerals mt-1" data-testid="preview-total">
            {preview ? preview.total.toLocaleString() : "—"}
          </div>
          <div className="text-[11px] text-slate-400">
            of {preview ? preview.total_population.toLocaleString() : "—"} physios
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-[12.5px]">
              <span className="inline-flex items-center gap-1.5 text-emerald-700"><MessageSquareText size={12} /> WhatsApp reachable</span>
              <span className="numerals font-medium" data-testid="preview-wa">{preview ? preview.whatsapp_reachable.toLocaleString() : "—"}</span>
            </div>
            <div className="flex items-center justify-between text-[12.5px]">
              <span className="inline-flex items-center gap-1.5 text-sky-700"><Smartphone size={12} /> SMS reachable</span>
              <span className="numerals font-medium" data-testid="preview-sms">{preview ? preview.sms_reachable.toLocaleString() : "—"}</span>
            </div>
            <div className="flex items-center justify-between text-[12.5px]">
              <span className="inline-flex items-center gap-1.5 text-slate-500"><Users size={12} /> Opted out</span>
              <span className="numerals">{preview ? preview.opted_out.toLocaleString() : "—"}</span>
            </div>
          </div>

          {preview?.sample?.length > 0 && (
            <div className="mt-4 border-t border-slate-200 pt-3">
              <div className="small-caps text-slate-500 mb-1.5">Sample (first {preview.sample.length})</div>
              <div className="max-h-56 overflow-auto -mx-1 space-y-1">
                {preview.sample.map((s) => (
                  <div key={s.id} className="px-2 py-1.5 rounded hover:bg-slate-50 text-[12px] flex items-center justify-between">
                    <div>
                      <div className="text-slate-800">{s.name || s.mobile}</div>
                      <div className="text-[10.5px] text-slate-400">{s.city || "—"} · {s.lifecycle || "—"} · {s.tier}</div>
                    </div>
                    <div className="numerals text-[11px] text-slate-500">{(s.active_points || 0).toLocaleString()} pts</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Export helpers for parent consumers
export { blankGroup, blankRule };
